import { Controller, type FieldValues } from 'react-hook-form';
import { InputNumber, type InputNumberProps } from 'antd';
import isUndefined from 'lodash/isUndefined';

import {
  extractPosFormItemProps,
  type PosFormComponentType,
} from './FormUtils.ts';
import { PosFormItem } from './PosFormItem.tsx';

type InputNumberValueType = string | number;

export type PosInputNumberProps<
  TFieldValues extends FieldValues = FieldValues,
  Value extends InputNumberValueType = InputNumberValueType,
> = PosFormComponentType<InputNumberProps<Value>, TFieldValues>;

/**
 * @component
 * @description Number input with React-Hook-Form integration
 * @see {@link https://ant.design/components/input-number | AntD InputNumber Component}
 *
 * @param PosInputNumberProps.control Control field from React-Hook-Form useForm
 * @remarks If an invalid control from useForm is passed then the Input will behave as an uncontrolled component and field name will not be type-safe
 * @param PosInputNumberProps.name Name of form field
 * @example
 * ```tsx
 * // Controlled (RHF) usage:
 * const { t } = useTranslation();
 * const { control } = useForm<FinanceForm>();
 *
 * <PosInputNumber
 *   control={control}
 *   name="amount"
 *   label={t('common.financial.amount')}
 * />
 *
 * // Uncontrolled (No RHF) usage:
 * const [amount, setAmount] = useState<number>();
 *
 * <PosInputNumber
 *   onChange={setAmount}
 *   value={amount}
 * />
 * ```
 */
export const PosInputNumber = <
  TFieldValues extends FieldValues = FieldValues,
  Value extends InputNumberValueType = InputNumberValueType,
>({
  control,
  name,
  children,
  ...props
}: PosInputNumberProps<TFieldValues, Value>) => {
  const isControlled = !!control;
  const ariaLabel = typeof props.label === 'string' ? props.label : name;

  const { posFormItemProps, rest: inputNumberProps } =
    extractPosFormItemProps(props);

  const onControlledChange = (
    value: string | number | null | undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (...event: any[]) => void
  ) => {
    const controlledValue = isUndefined(value)
      ? typeof value === 'string'
        ? ''
        : null
      : value;
    onChange(controlledValue);
  };

  if (isControlled)
    return (
      <PosFormItem control={control} name={name} {...posFormItemProps}>
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, ...field } }) => (
            <InputNumber
              {...inputNumberProps}
              {...field}
              onChange={(value) => onControlledChange(value, onChange)}
              data-testid={name}
              aria-label={ariaLabel}
            />
          )}
        />
      </PosFormItem>
    );

  return <InputNumber name={name} {...props} />;
};
